// @ts-nocheck
import { mapToElementState } from '@src/renderer/ElementState';
import {
  applyDeclarativePlacements,
  getTrackElementsFromEditor,
} from '@src/services/timeline/editing';
import {
  useEditor,
  useFeatureFlags,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { ElementPlacementController } from '@src/utility/ElementPlacementController';
import { generateSnapThreshold } from '@src/utility/elementSnapping';
import { observer } from 'mobx-react-lite';
import React, { Fragment, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import applyImageOptimizations from '../../utility/applyImageOptimizations';
import { inDebugMode } from '../../utility/debug';
import { extractErrorInfo } from '../../utility/error';
import { getElementPreviewMeta } from '../../utility/extraElementData';
import { LogManager } from '../../utility/Logger';
import {
  getTimelineKindColor,
  resolveTimelinePreview,
} from '../../utility/timelinePreview';
import {
  getElementTrackType,
  isCompatibleTrackType,
} from '../../utility/trackTypes';
import {
  areVolumeKeyPointsSupportedBy,
  getVolumeKeyPointsOf,
} from '../../utility/volumeKeyPoints';
import DebugModal from '../debug/DebugModal';
import { Draggable } from '../Draggable';
import { ResizeHandle } from './controls/ResizeHandle';
import { TimelineAudioElement } from './TimelineAudioElement';
import TimelineBrollElement from './TimelineBrollElement';
import { TimelineFrameThumbnails } from './TimelineFrameThumbnails';
import { TimelineGraphicElement } from './TimelineGraphicElement';
import { TimelineTextElement } from './TimelineTextElement';
import { TrimGhostLayer } from './TrimGhostLayer';

import type { ElementState } from '../../renderer/ElementState';
import type { Story } from '../../types.ts/story';
import type { TrackType } from '../../utility/trackTypes';

interface TimelineElementProps {
  element: ElementState;
  allElements: ElementState[];
  story?: Story;
  /** Track type for drag-and-drop compatibility checking */
  trackType?: TrackType;
  onContextMenu?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    el: ElementState,
  ) => void;
  onElementCrtlClick: (element: ElementState) => void;
}

export const PADDING_LEFT_COMPENSATION = 14;

/**
 * Pull drag-proposed (time, trim_start, duration) back within the bounds
 * of the previous and next elements on the same track. Keeps both the
 * trim-contract and move-drag paths consistent.
 */
const clampToNeighbors = (
  values: { time: number; trim_start: number; duration: number },
  prevElement: ElementState | undefined,
  nextElement: ElementState | undefined,
): { time: number; trim_start: number; duration: number } => {
  let { time, trim_start, duration } = values;
  if (prevElement && prevElement.globalTime + prevElement.duration > time) {
    const overlap = prevElement.globalTime + prevElement.duration - time;
    trim_start += overlap;
    duration -= overlap;
    time = prevElement.globalTime + prevElement.duration;
  }
  if (nextElement && nextElement.globalTime < time + duration) {
    duration = nextElement.globalTime - time;
  }
  return { time, trim_start, duration };
};
const logger = LogManager.getLogger('timeline:TimelineElement');

/**
 * Compute the global time corresponding to a click within a timeline element.
 * Must be called synchronously (reads `e.currentTarget`) before any await.
 */
function calculateClickTime(
  e: React.MouseEvent,
  element: ElementState,
  timelineScale: number,
): number {
  const elementRect = e.currentTarget.getBoundingClientRect();
  const clickOffsetX = e.clientX - elementRect.left;
  return element.globalTime + clickOffsetX / timelineScale;
}

/**
 * Navigate the playhead to the clicked position within a timeline element.
 * Used when the playhead is outside the element's time range on single-select click.
 */
async function navigateToClickedTime(
  clickTime: number,
  videoCreator,
): Promise<boolean> {
  try {
    await videoCreator.setTime(clickTime, true, false);
    return true;
  } catch (error) {
    logger.error(
      'Failed to navigate to element time on click',
      extractErrorInfo(error),
    );
    return false;
  }
}

const getStylePositionForElementPlacement = (
  placement,
  timelineScale,
  timelineElementWidth,
) => ({
  top: placement.yOffset || null,
  left: placement.globalTime * timelineScale + PADDING_LEFT_COMPENSATION,
  width: timelineElementWidth,
});

export const TimelineElement: React.FC<TimelineElementProps> = observer(
  props => {
    const videoCreator = useVideoCreatorStore();
    const editorStore = useEditor();
    const videoSourceStore = useVideoSource();
    const timelineStore = videoCreator.timelineStore;
    const { element, story, trackType, allElements } = props;
    const overridenState =
      timelineStore.overrideTimelineElementsState[element.source.id];
    const activeElement =
      // videoCreator.activeElementIds.includes(element.source.id) ||
      timelineStore.isTimelineElementActive(element.source.id);
    const activeTrack = element.track === videoCreator.selectedTrack;
    const active = activeElement || activeTrack;
    const timelineScale = videoCreator.timelineScale;
    const [isDragging, setIsDragging] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedElement, setSelectedElement] = useState<ElementState | null>(
      null,
    );
    const isTrimmingThisElement = timelineStore.isTrimming(element.source.id);

    const nextElement = React.useMemo(
      () => allElements.find(el => el.globalTime > element.globalTime),
      [allElements],
    );

    //@ts-ignore
    const prevElement = React.useMemo(
      () => allElements.findLast(el => el.globalTime < element.globalTime),
      [allElements],
    );

    const flags = useFeatureFlags();
    const {
      enableTimelineSnapping = false,
      enableMovingBetweenTracks = false,
    } = flags;

    useEffect(() => {
      if (element.source.type !== 'video') return;
      if (videoCreator.isOriginalVideoElement(element.source)) {
        setThumbnailUrl(story?.originalVideo?.video?.thumbnailUrl);
      } else {
        const storyVideo = story?.storyArtifactsVideo?.find(
          s => s.url === element.source.source,
        );
        const showcase =
          story?.primaryShowcase || story?._allReferencingShowcases?.[0];
        const orgVideo = showcase?.organizationArtifactsVideo?.find(
          s => s.url === element.source.source,
        );
        setThumbnailUrl(
          storyVideo?.video?.thumbnailUrl ||
            orgVideo?.video?.thumbnailUrl ||
            '',
        );
      }
    }, []);

    const reconcileKeyPointsToNewDuration = () => {
      const keyPoints = getVolumeKeyPointsOf(videoCreator, element);
      if (!areVolumeKeyPointsSupportedBy(element) || !keyPoints?.length) return;
      videoCreator.rootStore.videoSourceStore.onNextSourceRefresh(() => {
        videoCreator.applyVolumeKeyPoints(element.source.id, keyPoints, false);
      });
    };

    const placement = {
      ...element,
      ...overridenState,
    };

    // Use centralized track type utilities
    const elementTrackType = getElementTrackType(
      element,
      videoCreator,
      trackType,
    );
    const isLinkedAudio = trackType === 'linked-audio';
    const isKaraoke = elementTrackType === 'karaoke';
    // Non-trim drag path (e.g. move) — original behavior.
    const applyPlacementAndKeyPoints = (side?: 'start' | 'end') => {
      if (side === 'end') reconcileKeyPointsToNewDuration();

      // Trim drags buffered values into trimPreview while the Bar stayed
      // visually frozen. commitTrimPreview fires the editor command
      // synchronously so the element's new size and rippled neighbors
      // land in the same MobX transaction.
      if (timelineStore.getTrimPreview(element.source.id)) {
        timelineStore.commitTrimPreview(element.source.id);
        return;
      }

      timelineStore.applyPlacements(enableMovingBetweenTracks);
    };

    const onTimeDurationChange = (time, trim_start, duration) => {
      if (
        (videoCreator.isOriginalVideoElement(element.source) ||
          element.source.type === 'audio') &&
        element.mediaDuration &&
        element.mediaDuration + element.trimStart < duration + trim_start
      ) {
        duration = element.mediaDuration;
      }

      if (isTrimmingThisElement && !isKaraoke) {
        // Trim drag (contract OR extend): buffer raw drag values in
        // trimPreview so the Bar and renderer stay visually frozen. Only
        // the ghost overlay animates. Commit runs on mouse-up via
        // commitTrimPreview. Contract clamps to neighbors; extend skips
        // clamps so the ripple-aware trim command can shift them.
        // Karaoke/caption bubbles are excluded — the ghost-preview commit
        // path doesn't move them, so they use the original live-override
        // trim path below (the pre-ghost behavior).
        const isExtending = duration > element.duration;
        if (!isExtending) {
          ({ time, trim_start, duration } = clampToNeighbors(
            { time, trim_start, duration },
            prevElement,
            nextElement,
          ));
        }
        // Clear any stale override so the Bar reads the snapshot element.
        // Return early — the trailing applyPlacementOverrideToElement must
        // not run during a trim drag or the Bar will resize despite the intent
        // to keep it visually frozen until commit.
        timelineStore.clearPlacementOverride(element.source.id);
        timelineStore.setTrimPreview(element.source.id, {
          globalTime: time,
          trimStart: trim_start,
          duration,
        });
        return;
      } else {
        // Non-trim drag fallback (shouldn't fire from ResizeHandle but
        // kept for safety): original override-write path with neighbor
        // clamps.
        ({ time, trim_start, duration } = clampToNeighbors(
          { time, trim_start, duration },
          prevElement,
          nextElement,
        ));
        timelineStore.applyPlacementOverrideToElement(element.source.id, {
          globalTime: time,
          trimStart: trim_start,
          duration,
        });
      }

      if (nextElement && nextElement.globalTime < time + duration) {
        duration = nextElement.globalTime - time;
      }

      timelineStore.applyPlacementOverrideToElement(element.source.id, {
        globalTime: time,
        trimStart: trim_start,
        duration,
      });
    };

    const onDrag = (e, data, context) => {
      e.preventDefault();
      if (e.type === 'mousemove') setIsDragging(true);
      const yOffset = enableMovingBetweenTracks ? data.deltaY : 0;
      const target = e.target as HTMLElement | null;
      const trackEl = target?.closest<HTMLElement>(
        '.timeline-track-drag-target',
      );
      const targetTrackId = trackEl?.dataset.trackNumber;
      const targetTrackType = trackEl?.dataset.trackType as
        | TrackType
        | undefined;

      // Only allow moving to compatible track types
      let targetTrackNumber: number;
      if (
        targetTrackId &&
        isCompatibleTrackType(elementTrackType, targetTrackType || null)
      ) {
        targetTrackNumber = parseInt(targetTrackId);
      } else {
        // Revert to original track if incompatible
        targetTrackNumber = element.source.track;
      }

      let draggedElements;

      if (!activeElement) {
        draggedElements = [element];
      } else {
        draggedElements = [
          ...timelineStore
            .getActiveTimelineElementIds()
            .map(id => editorStore.editor.timeline.getElement(id))
            .filter(Boolean)
            .map(el => mapToElementState(el.toJSON())),
        ];
      }

      // Use declarative approach for single original video element drags
      const isSingleElement = draggedElements.length === 1;
      const isOriginalVideo = videoCreator.isOriginalVideoElement(
        element.source,
      );

      if (isSingleElement && isOriginalVideo && context?.startX !== undefined) {
        // Calculate TOTAL offset from drag start (not per-frame delta)
        const totalTimeOffset = (data.x - context.startX) / timelineScale;

        // Get track elements from EditorStore (source of truth)
        const trackElements = getTrackElementsFromEditor(
          editorStore.tracksMap,
          element.track,
        );

        applyDeclarativePlacements({
          draggedElement: element,
          totalTimeOffset,
          snapThreshold: generateSnapThreshold(timelineScale),
          trackElements,
          timelineStore,
          videoCreatorStore: videoCreator,
          snapshotBoundaries: context.snapBoundaries ?? [],
          originalGlobalTime: context.originalGlobalTime ?? element.globalTime,
        });
      } else {
        // Fall back to existing controller for multi-element or non-video drags
        // Compute deltaX ourselves (fixes react-draggable deltaX bug)
        const deltaX = data.x - (context?.lastX ?? data.x);
        if (context) context.lastX = data.x;
        const timeOffset = deltaX / timelineScale;
        const elementPlacementController = new ElementPlacementController(
          videoCreator,
        );
        elementPlacementController.setElementsTimelinePosition(
          draggedElements,
          timeOffset,
          yOffset,
          targetTrackNumber,
        );
      }
    };

    // PLAT-11816 — graphics, shapes, photos and logos get a typed preview
    // (rail + tinted fill + cropped image tile + glyph + label). Video, audio
    // and text keep their own block.
    const imageChild =
      element.source.type === 'composition'
        ? element.elements?.find(e => e.source?.type === 'image')
        : element.source.type === 'image'
          ? element
          : undefined;
    const preview = resolveTimelinePreview({
      trackType: elementTrackType,
      meta: getElementPreviewMeta(videoCreator.currentVideo, element.source.id),
      imageChildUrl: imageChild
        ? applyImageOptimizations(imageChild.source?.source, { height: 60 })
        : undefined,
    });

    let color = 'var(--surface-elevated)';
    switch (element.source.type) {
      case 'audio':
        color = getTimelineKindColor('audio');
        break;
      case 'video':
        color = getTimelineKindColor('video');
        break;
      case 'image':
        color = videoCreator.isLogoElement(element)
          ? getTimelineKindColor('logo')
          : getTimelineKindColor('photo');
        break;
      case 'text':
        color = getTimelineKindColor('text');
        break;
      case 'composition':
        if (videoCreator.isImageElementComposition(element)) {
          color = getTimelineKindColor('photo');
        } else if (videoCreator.isVideoElementComposition(element)) {
          color = getTimelineKindColor('video');
        }
        break;
      default:
        break;
    }
    if (element.source.track === KARAOKE_TRACK_NUMBER)
      color = getTimelineKindColor('text');
    if (preview) color = preview.backgroundColor;

    const timelineElementWidth =
      (placement.duration - element.exitDuration) * timelineScale;

    const isBroll = !videoCreator.isOriginalVideoElement(element.source);

    const isComposition =
      element.source.type === 'composition' &&
      !videoCreator.isImageElementComposition(element);

    const handleSelectOutsidePlayhead = async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ): Promise<void> => {
      const clickTime = calculateClickTime(e, element, timelineScale);
      const isDoubleClick = e.detail > 1;

      // Exit any drilled-into composition first: while one is active the
      // renderer scopes setTime to it, so the seek below would be clamped to
      // the composition boundary instead of landing at clickTime (and the
      // element wouldn't be visible/selectable there).
      // ---
      // Also prevents the reaction in VideoCreatorStore.ts that handles
      // single-element multi-scene reselection logic from throwing a "not
      // found" error
      await videoCreator.deselectActiveComposition();

      // The clicked element may be windowed-out of the source. Seeking onto it
      // triggers a layout refresh (windowing or hybrid-adaptive)
      const willRefresh =
        !videoCreator.isPlaying &&
        !videoCreator.isScrubbing &&
        (editorStore.needsWindowingRefresh(clickTime) ||
          editorStore.needsHybridAdaptiveRefresh(clickTime));
      const sourceRefresh = willRefresh
        ? videoSourceStore.waitForNextStateChange()
        : null;

      const navigated = await navigateToClickedTime(clickTime, videoCreator);
      if (navigated) {
        await sourceRefresh;
        videoCreator.setActiveElement(element, false, isDoubleClick);
      }
    };

    return (
      <Fragment>
        {inDebugMode() && isModalOpen && (
          <DebugModal
            title="Timeline Element Debug"
            content={selectedElement}
            onClose={() => {
              setIsModalOpen(false);
            }}
          />
        )}
        <Draggable
          cancel={'.volume-key-point'}
          bounds={'#timeline-draggable-bounds'}
          onStart={(e, data) => {
            e.preventDefault();
            if (
              !e.shiftKey &&
              !timelineStore
                .getActiveTimelineElementIds()
                .includes(element.source.id)
            ) {
              const newActiveElementIds = [element.source.id];
              timelineStore.setActiveTimelineElements(...newActiveElementIds);
            }

            if (!enableTimelineSnapping) {
              return {
                startX: data.x,
                startY: data.y,
                lastX: data.x,
                originalGlobalTime: element.globalTime,
              };
            }
            // Pre-calculate snap boundaries for efficient snapping during drag
            const snapBoundaries = videoCreator.createSnapBoundariesForDrag(
              videoCreator.activeElementIds,
            );
            return {
              startX: data.x,
              startY: data.y,
              lastX: data.x,
              snapBoundaries,
              originalGlobalTime: element.globalTime,
            };
          }}
          onDrag={onDrag}
          onStop={(e, data, context) => {
            e.preventDefault();
            videoCreator.openPhotoElementReplacementModal = null;
            if (!context) return;
            // Compute deltaX ourselves (fixes react-draggable deltaX bug)
            const deltaX = data.x - (context.lastX ?? data.x);
            const timeOffset = deltaX / timelineScale;
            const yOffset = enableMovingBetweenTracks ? data.deltaY : 0;

            const draggedElements = [
              element,
              ...timelineStore
                .getActiveTimelineElementIds()
                .filter(el => el !== element.source.id)
                .map(id => allElements.find(el => el.source.id === id))
                .filter(Boolean),
            ];

            const isSingleElement = draggedElements.length === 1;
            const isOriginalVideo = videoCreator.isOriginalVideoElement(
              element.source,
            );
            const usedDeclarativePath = isSingleElement && isOriginalVideo;

            // For declarative path, do final placement with overlap resolution
            if (usedDeclarativePath && context?.startX !== undefined) {
              const totalTimeOffset = (data.x - context.startX) / timelineScale;
              const trackElements = getTrackElementsFromEditor(
                editorStore.tracksMap,
                element.track,
              );

              applyDeclarativePlacements({
                draggedElement: element,
                totalTimeOffset,
                snapThreshold: generateSnapThreshold(timelineScale),
                trackElements,
                timelineStore,
                videoCreatorStore: videoCreator,
                snapshotBoundaries: context.snapBoundaries ?? [],
                originalGlobalTime:
                  context.originalGlobalTime ?? element.globalTime,
                isFinalPlacement: true,
              });
            } else if (
              (context.startX !== data.x || context.startY !== data.y) &&
              !usedDeclarativePath
            ) {
              // Legacy path for non-declarative drags
              const target = e.target as HTMLElement | null;
              const trackEl = target?.closest<HTMLElement>(
                '.timeline-track-drag-target',
              );
              const targetTrackId = trackEl?.dataset.trackNumber;
              const targetTrackType = trackEl?.dataset.trackType as
                | TrackType
                | undefined;
              let targetTrackNumber;

              // Only allow moving to compatible track types
              if (
                enableMovingBetweenTracks &&
                targetTrackId &&
                draggedElements.length === 1 &&
                isCompatibleTrackType(elementTrackType, targetTrackType || null)
              ) {
                targetTrackNumber = parseInt(targetTrackId);
              } else {
                // Revert to original track if incompatible or multi-select
                targetTrackNumber = element.source.track;
              }

              const elementPlacementController = new ElementPlacementController(
                videoCreator,
              );
              elementPlacementController.setElementsTimelinePosition(
                draggedElements,
                timeOffset,
                yOffset,
                targetTrackNumber,
                true,
              );
            }

            videoCreator.setSnapGuide(false);

            applyPlacementAndKeyPoints();

            if (e.type === 'mouseup') {
              setTimeout(() => {
                setIsDragging(false);
              }, 100);
            }
          }}
        >
          {ref => (
            <Bar
              ref={ref}
              active={active}
              color={color}
              audio={element.source.type === 'audio'}
              linkedAudio={isLinkedAudio}
              isKaraoke={element.source.track === KARAOKE_TRACK_NUMBER}
              linkedVideo={
                element.source.type === 'video' &&
                !isLinkedAudio &&
                videoCreator.isOriginalVideoElement(element.source)
              }
              isText={
                element.source.type === 'text' ||
                element.source.track === KARAOKE_TRACK_NUMBER
              }
              isShape={element.source.type === 'shape'}
              isComposition={isComposition}
              isLogo={videoCreator.isLogoElement(element)}
              isTypedPreview={!!preview}
              isDragging={isDragging}
              style={getStylePositionForElementPlacement(
                placement,
                timelineScale,
                timelineElementWidth,
              )}
              onClick={async e => {
                if (isDragging) return;
                if (e.ctrlKey) {
                  props.onElementCrtlClick(element);
                  setSelectedElement(element);
                }
                // Set timeline element as active in TimelineStore (direct timeline element)
                let newActiveElementIds = [];
                if (e.shiftKey) {
                  newActiveElementIds.push(
                    ...timelineStore.getActiveTimelineElementIds(),
                  );
                  if (newActiveElementIds.includes(element.source.id)) {
                    newActiveElementIds = newActiveElementIds.filter(
                      id => id !== element.source.id,
                    );
                  } else {
                    newActiveElementIds.push(element.source.id);
                  }
                } else {
                  newActiveElementIds = [element.source.id];
                }
                timelineStore.setActiveTimelineElements(...newActiveElementIds);
                if (
                  videoCreator.time >= element.globalTime &&
                  videoCreator.time <= element.globalTime + element.duration
                ) {
                  videoCreator.setActiveElement(
                    element,
                    e.shiftKey,
                    e.detail > 1,
                  );
                } else if (!e.shiftKey) {
                  await handleSelectOutsidePlayhead(e);
                } else {
                  videoCreator.selectedTrack = -1;
                }
              }}
            >
              <ElementContainer
                imageUrl={
                  !preview &&
                  !isLinkedAudio &&
                  !(
                    element.source.type === 'video' &&
                    videoCreator.isOriginalVideoElement(element.source)
                  ) &&
                  !(element.source.type === 'video' && isBroll)
                    ? thumbnailUrl ||
                      applyImageOptimizations(
                        videoCreator.getImageElement(element)?.source?.source,
                        {
                          height: videoCreator.isLogoElement(element) ? 30 : 70,
                        },
                      )
                    : ''
                }
                audio={element.source.type === 'audio'}
                linkedAudio={isLinkedAudio}
                linkedVideo={element.source.type === 'video' && !isLinkedAudio}
              >
                {element.source.type === 'audio' ? (
                  <TimelineAudioElement
                    element={element}
                    onContextMenu={props.onContextMenu}
                  />
                ) : element.source.type === 'video' ? (
                  isBroll ? (
                    <TimelineBrollElement
                      element={element}
                      thumbnail={thumbnailUrl}
                      elementWidth={timelineElementWidth}
                      height={70}
                    />
                  ) : (
                    <>
                      {story?.id &&
                        videoCreator.isOriginalVideoElement(element.source) &&
                        !isLinkedAudio && (
                          <TimelineFrameThumbnails
                            storyId={story.id}
                            videoId={
                              videoCreator.currentVideo?.id ||
                              story.originalVideo.id
                            }
                            elementId={element.source.id}
                            trimStart={element.trimStart || 0}
                            duration={element.duration}
                            elementWidth={timelineElementWidth}
                            height={55}
                            videoWidth={story.originalVideo?.width}
                            videoHeight={story.originalVideo?.height}
                            fallbackThumbnailUrl={thumbnailUrl}
                            hasThumbnails={videoCreator.story?.useAws}
                          />
                        )}
                      {isLinkedAudio ? (
                        <TimelineAudioElement element={element} />
                      ) : null}
                      {!thumbnailUrl ? (
                        <VideoPlayer src={element.source.source} />
                      ) : null}
                    </>
                  )
                ) : preview ? (
                  <TimelineGraphicElement
                    preview={preview}
                    elementWidth={timelineElementWidth}
                  />
                ) : element.source.type === 'text' ||
                  element.source.track === KARAOKE_TRACK_NUMBER ? (
                  <TimelineTextElement element={element} />
                ) : null}
              </ElementContainer>
            </Bar>
          )}
        </Draggable>
        {isTrimmingThisElement && !isKaraoke ? (
          <TrimGhostLayer
            // Snapshot = the pre-drag element values, untouched by override.
            snapshotGlobalTime={element.globalTime}
            snapshotDuration={element.duration}
            snapshotTrimStart={element.trimStart || 0}
            // Current = live drag state from trimPreview (Bar stays frozen
            // for both contract and extend until commit).
            currentGlobalTime={
              timelineStore.getTrimPreview(element.source.id)?.globalTime ??
              placement.globalTime
            }
            currentDuration={
              timelineStore.getTrimPreview(element.source.id)?.duration ??
              placement.duration
            }
            currentTrimStart={
              timelineStore.getTrimPreview(element.source.id)?.trimStart ??
              placement.trimStart ??
              element.trimStart ??
              0
            }
            waveformUrl={
              // `isOriginalVideoElement` is too strict — it checks exact URL
              // prefix against selfHostedVideo/datoHostedVideo and returns
              // false for clips hosted at a different CDN path even when they
              // share the story's primary audio. For any video element,
              // prefer `originalVideoAudioUrl` (that's where backend waveform
              // data exists). Audio elements use their own source.
              element.source.type === 'video' &&
              videoCreator.originalVideoAudioUrl
                ? videoCreator.originalVideoAudioUrl
                : element.source.source
            }
            elementType={element.source.type}
            storyId={story?.id}
            videoId={videoCreator.currentVideo?.id || story?.originalVideo?.id}
            videoWidth={story?.originalVideo?.width}
            videoHeight={story?.originalVideo?.height}
            extendContentMode={(() => {
              // Audio row: draw waveform.
              if (element.source.type === 'audio') return 'waveform';
              // Linked-audio copy of a video element: draw waveform.
              if (element.source.type === 'video' && isLinkedAudio)
                return 'waveform';
              // Primary video row: draw source frame thumbnails.
              if (element.source.type === 'video' && !isLinkedAudio)
                return 'thumbnails';
              return 'none';
            })()}
            barTop={isLinkedAudio ? 0 : 4}
            barHeight={(() => {
              if (isLinkedAudio) return 36;
              if (
                element.source.type === 'text' ||
                videoCreator.isLogoElement(element) ||
                element.source.type === 'shape' ||
                isComposition ||
                preview
              )
                return 30;
              if (
                element.source.type === 'video' &&
                !isLinkedAudio &&
                videoCreator.isOriginalVideoElement(element.source)
              )
                return 55;
              return 70;
            })()}
          />
        ) : null}
        {!isLinkedAudio ? (
          <>
            <ResizeHandle
              element={element}
              side="start"
              time={element.localTime}
              onChange={onTimeDurationChange}
              onTrimStart={() => timelineStore.beginTrim(element.source.id)}
              onTrimStop={() => timelineStore.endTrim(element.source.id)}
              onComplete={() => applyPlacementAndKeyPoints('start')}
            />
            <ResizeHandle
              element={element}
              side="end"
              time={element.localTime + element.duration}
              onChange={onTimeDurationChange}
              onTrimStart={() => timelineStore.beginTrim(element.source.id)}
              onTrimStop={() => timelineStore.endTrim(element.source.id)}
              onComplete={() => applyPlacementAndKeyPoints('end')}
            />
          </>
        ) : null}
      </Fragment>
    );
  },
);

const Bar = styled.div<{
  color: string;
  audio: boolean;
  linkedAudio: boolean;
  linkedVideo: boolean;
  isText: boolean;
  isLogo: boolean;
  isShape: boolean;
  isComposition: boolean;
  /** PLAT-11816 — carries a typed preview, so it takes the 30px row */
  isTypedPreview: boolean;
  isKaraoke: boolean;
  active: boolean;
  isDragging: boolean;
}>`
  position: absolute;
  /* Base z-index ensures elements are always above their track;
   * active elements get higher z-index to stay on top of other elements */
  z-index: 1;
  /* Prevent selection locally instead of mutating document.body, relates to
   * enableUserSelectHack */
  user-select: none;

  /* When dragging, disable pointer events on self and ALL descendants to allow
   * the underlying track element to receive the drop target detection */
  ${props =>
    props.isDragging &&
    css`
      pointer-events: none;
      & * {
        pointer-events: none !important;
      }
    `}

  height: ${props => {
    if (props.linkedAudio) return '36px';
    if (
      props.isText ||
      props.isLogo ||
      props.isShape ||
      props.isComposition ||
      props.isTypedPreview
    )
      return '30px';
    if (props.linkedVideo) return '55px';
    return '70px';
  }};
  -webkit-box-align: center;
  align-items: center;
  ${props =>
    props.isText &&
    css`
      background: ${props =>
        props.active ? 'var(--foreground)' : props.color};
    `}

  ${props =>
    props.isKaraoke &&
    css`
      background: ${props => (props.active ? 'var(--primary)' : props.color)};
    `}

      ${props =>
    (!props.isKaraoke || !props.isText) &&
    css`
      background: ${props => (props.linkedVideo ? 'transparent' : props.color)};
    `}

  border-top-left-radius: ${props => (!props.linkedAudio ? '6px' : '0px')};
  border-top-right-radius: ${props => (!props.linkedAudio ? '6px' : '0px')};
  border-bottom-left-radius: ${props => (!props.linkedVideo ? '5px' : '0px')};
  border-bottom-right-radius: ${props => (!props.linkedVideo ? '5px' : '0px')};
  display: flex;
  box-sizing: border-box;
  overflow: hidden;

  ${props =>
    props.active &&
    css`
      z-index: 2;
    `}

  ${props =>
    props.active && !props.isKaraoke && !props.linkedAudio
      ? css`
          &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 20px;
            height: 20px;
            background-color: var(--foreground);
            clip-path: polygon(0 0, 100% 0, 100% 100%);
            z-index: 1;
          }
        `
      : null}
  ${props =>
    props.active && !props.isKaraoke
      ? css`
          &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            border: 2px solid var(--foreground);
            border-top-left-radius: ${!props.linkedAudio ? '6px' : '0px'};
            border-top-right-radius: ${!props.linkedAudio ? '6px' : '0px'};
            border-bottom-left-radius: ${!props.linkedVideo ? '5px' : '0px'};
            border-bottom-right-radius: ${!props.linkedVideo ? '5px' : '0px'};
            border-top-width: ${!props.linkedAudio ? '2px' : '0px'};
            border-bottom-width: ${!props.linkedVideo ? '2px' : '0px'};
            pointer-events: none;
          }
        `
      : null}
`;

const ElementContainer = styled.div<{
  audio: boolean;
  imageUrl?: string;
  linkedAudio: boolean;
  linkedVideo: boolean;
}>`
  pointer-events: none;
  height: 100%;
  width: ${props => (props.audio ? 'calc(100% + 4px)' : '100%')};
  background-image: ${props => `url("${props.imageUrl}")`};
  background-repeat: repeat-x;
  background-size: auto 100%;
  border-top-left-radius: ${props => (!props.linkedAudio ? '4px' : '0px')};
  border-top-right-radius: ${props => (!props.linkedAudio ? '4px' : '0px')};
  border-bottom-left-radius: ${props => (!props.linkedVideo ? '5px' : '0px')};
  border-bottom-right-radius: ${props => (!props.linkedVideo ? '5px' : '0px')};
  position: relative;
`;

const VideoPlayer = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;
